import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Compass,
  Cpu,
  Globe2,
  Handshake,
  Hammer,
  HardHat,
  Layers,
  Map as MapIcon,
  PackageSearch,
  Recycle,
  Smartphone,
  Sparkles,
} from 'lucide-react';
import { Button } from '../components/ui/button';

const NowItems = [
  { icon: Recycle, title: 'Curated marketplace', body: 'Salvaged building materials with images, filters, and 3D models.' },
  { icon: Layers, title: '3D models', body: 'Free Tier-1 textured prisms + AI Tier-2 models from your photos.' },
  { icon: Globe2, title: 'Branded storefronts', body: 'Each supplier gets a public landing page at supplier.patinamart.com.' },
  { icon: Compass, title: 'Geolocation search', body: 'Browse listings by distance from anywhere — works on mobile too.' },
  { icon: Handshake, title: 'Trade Exchange', body: 'Supplier-only B2B market for surplus stock and giveaways.' },
  { icon: Hammer, title: 'Revit plugin', body: 'Designers can drag salvaged stock straight into Revit projects.' },
  {
    icon: Smartphone,
    title: 'Supplier Wingman (mobile)',
    body: 'A pocket companion for yard staff — instant enquiry alerts, one-tap photo listings, replies on the move.',
    link: '/supplier-app',
    linkLabel: 'Learn more',
  },
];

const NextItems = [
  {
    icon: Cpu,
    title: 'ArchiCAD & SketchUp plugins',
    body: 'Same in-tool drag-in experience as the Revit plugin — bring salvaged stock straight into ArchiCAD and SketchUp projects.',
    tag: 'Plugins',
  },
  {
    icon: PackageSearch,
    title: 'Curated external feeds',
    body: 'Selected listings from FB Marketplace, Gumtree and Craigslist filtered to building-materials categories — no auto-import of off-topic stock.',
    tag: 'Aggregation',
  },
  {
    icon: HardHat,
    title: 'Supplier specialisations',
    body: 'Yards declare what they\'re known for (light fittings, doors, tiles…) so the network can self-organise around expertise.',
    tag: 'Network',
  },
];

const FutureItems = [
  {
    icon: MapIcon,
    title: 'Building-stock heatmap',
    body: 'Anonymised, indicative capacity forecast built from public planning data (SAPPA) — gives the community visibility into upcoming material flows from anticipated demolitions.',
  },
  { icon: Sparkles, title: 'AR preview', body: 'Hold up your phone in a room and drop the salvaged piece in to scale.' },
  { icon: Smartphone, title: 'Buyer mobile app', body: 'Wishlist & alerts, push-notifications when matches drop.' },
];

const RoadmapSection = ({ label, accent, items }) => (
  <div className="rounded-xl border border-border/20 bg-card/30 p-6 md:p-8">
    <p className={`text-xs uppercase tracking-[0.2em] mb-4 ${accent}`}>{label}</p>
    <div className="space-y-5">
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <div key={item.title} className="flex gap-4">
            <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-secondary/50 border border-border/30 flex items-center justify-center">
              <Icon className="w-5 h-5 text-foreground/80" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h4 className="font-medium text-foreground">{item.title}</h4>
                {item.tag && (
                  <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-current/30 ${accent}`}>
                    {item.tag}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">{item.body}</p>
              {item.link && (
                <Link
                  to={item.link}
                  className="inline-flex items-center gap-1 text-xs text-accent hover:underline mt-2"
                  data-testid={`roadmap-link-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                >
                  {item.linkLabel || 'Learn more'} <ArrowRight className="w-3 h-3" />
                </Link>
              )}
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

export default function AboutPage() {
  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="about-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="mb-16">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3">About PatinaMart</p>
          <h1 className="font-playfair text-4xl md:text-6xl font-bold text-foreground leading-tight mb-6">
            A circular economy for
            <br />
            <span className="text-accent">building materials.</span>
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl leading-relaxed">
            Every demolished structure is an inventory of useful material. PatinaMart connects salvage suppliers
            with designers, builders, and homeowners — with first-class digital tools so reclaimed materials
            specify just as easily as anything new.
          </p>
          <div className="flex gap-3 mt-8">
            <Link to="/marketplace">
              <Button className="btn-gold" data-testid="about-browse-btn">
                Browse marketplace <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/auth?type=supplier">
              <Button variant="outline" className="border-border/40" data-testid="about-list-btn">
                List your salvage yard
              </Button>
            </Link>
          </div>
        </div>

        {/* Why */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-20">
          <div className="rounded-xl border border-border/20 bg-card/40 p-6" data-testid="about-stat-1">
            <p className="text-3xl font-bold text-accent">~40%</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2">of landfill is</p>
            <p className="text-sm text-foreground">construction & demolition waste</p>
          </div>
          <div className="rounded-xl border border-border/20 bg-card/40 p-6" data-testid="about-stat-2">
            <p className="text-3xl font-bold text-accent">100%</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2">of our listings</p>
            <p className="text-sm text-foreground">have a real photo and exact location</p>
          </div>
          <div className="rounded-xl border border-border/20 bg-card/40 p-6" data-testid="about-stat-3">
            <p className="text-3xl font-bold text-accent">1%</p>
            <p className="text-xs uppercase tracking-widest text-muted-foreground mt-2">commission only</p>
            <p className="text-sm text-foreground">on completed sales — no quotes, clicks or listing fees</p>
          </div>
        </div>

        {/* Roadmap */}
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2">Development roadmap</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-3">
            What we've built — and what's next.
          </h2>
          <p className="text-muted-foreground max-w-2xl">
            A live snapshot of the roadmap. We share it openly so the community can shape the priorities.
            Have an idea? <Link to="/contact" className="text-accent hover:underline">Get in touch</Link>.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-12" data-testid="roadmap-grid">
          <div data-testid="roadmap-now">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-medium text-emerald-400">Now</span>
              <span className="text-xs text-muted-foreground/50">— shipped &amp; live</span>
            </div>
            <RoadmapSection label="Live today" accent="text-emerald-400" items={NowItems} />
          </div>
          <div data-testid="roadmap-next">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles className="w-5 h-5 text-amber-400" />
              <span className="text-sm font-medium text-amber-400">Next</span>
              <span className="text-xs text-muted-foreground/50">— in progress</span>
            </div>
            <RoadmapSection label="Coming soon" accent="text-amber-400" items={NextItems} />
          </div>
          <div data-testid="roadmap-future">
            <div className="flex items-center gap-2 mb-3">
              <Compass className="w-5 h-5 text-violet-400" />
              <span className="text-sm font-medium text-violet-400">Future</span>
              <span className="text-xs text-muted-foreground/50">— exploring</span>
            </div>
            <RoadmapSection label="Future" accent="text-violet-400" items={FutureItems} />
          </div>
        </div>
      </div>
    </div>
  );
}
